import { Chip, Stack, Typography, type ChipProps } from '@mui/material';
import { grey, amber, blue, purple, teal } from '@mui/material/colors';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import HelpOutline from '@mui/icons-material/HelpOutline';
import type { HotelBoardCategory } from '@bandido/shared/types/hotels/hotel-search-filters';
import type { HotelCancellationState } from '@bandido/shared/types/hotels/hotel-cancellation';
import { useTranslation } from 'react-i18next';

const boardColors = {
  room_only: grey,
  breakfast: amber,
  half_board: blue,
  full_board: purple,
  all_inclusive: teal,
  other: grey,
  unknown: grey,
};

export function HotelBoardLabel({
  category,
  ...props
}: { category: HotelBoardCategory } & Pick<ChipProps, 'onDelete' | 'className' | 'tabIndex'>) {
  const { t } = useTranslation();
  const palette = boardColors[category];
  return (
    <Chip
      {...props}
      size="small"
      label={t(`hotelSearch.boards.${category}`)}
      sx={(theme) => ({
        bgcolor: theme.palette.mode === 'dark' ? palette[900] : palette[50],
        color: theme.palette.mode === 'dark' ? palette[50] : palette[900],
        height: 'auto',
        minHeight: 24,
        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
      })}
    />
  );
}

export function HotelCancellationLabel({ state }: { state: HotelCancellationState }) {
  const { t } = useTranslation();
  const Icon = {
    free_now: CheckCircleOutline,
    penalty_applies: PaidOutlined,
    non_refundable: LockOutlined,
    unknown: HelpOutline,
  }[state];
  return (
    <Stack
      direction="row"
      gap={0.5}
      alignItems="center"
      sx={{
        color: state === 'free_now' ? 'success.main' : state === 'penalty_applies' ? 'warning.dark' : 'text.secondary',
      }}
    >
      <Icon sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true" />
      <Typography variant="body2" color="text.primary">
        {t(`hotelSearch.cancellationState.${state}`)}
      </Typography>
    </Stack>
  );
}
